import { useCallback, useMemo, useState } from "react";
import type { Dispatch, JSX, SetStateAction } from "react";

import { AlertCircle, CheckCircle, Circle, Clock, Loader, Sparkles } from "lucide-react";
import type { LucideIcon } from "lucide-react";

import { updateFixStatus } from "../api/persona";
import type {
  FixStatus,
  GapSeverity,
  PersonaDiagnosis,
  PersonaFix,
  PersonaGap,
} from "../api/personaTypes";
import { ALL, useRecommendationFilters } from "../hooks/useRecommendationFilters";
import type { RecommendationFilterSpec } from "../hooks/useRecommendationFilters";
import { EFFORT_CHIP_COLORS, EFFORT_FILTER_OPTIONS, EFFORT_LABELS } from "../utils/effort";
import { PRIORITY_ICON_COLORS } from "../utils/priority";
import type { PriorityLevel } from "../utils/priority";
import Chip from "./Chip";
import DiagnosisShell from "./DiagnosisShell";
import ImplementationGuideModal from "./ImplementationGuideModal";
import PriorityBadge from "./PriorityBadge";
import RecommendationEmptyState from "./RecommendationEmptyState";
import RecommendationFilterBar from "./RecommendationFilterBar";

/**
 * Gap severity in the vocabulary the shared priority badge speaks.
 *
 * The persona rules engine has no Critical tier — its worst finding is a "high" severity gap — so
 * the mapping is three levels onto the badge's top three.
 */
const SEVERITY_PRIORITY: Record<GapSeverity, PriorityLevel> = {
  high: "High",
  medium: "Medium",
  low: "Low",
};

const STATUS_LABELS: Record<FixStatus, string> = {
  open: "Open",
  in_progress: "In Progress",
  resolved: "Resolved",
};

/**
 * How each status looks: a traffic light, with an icon and a button treatment per state.
 *
 * Colour carries the urgency — red for work not started, orange for work under way, green for work
 * finished — which is the one progression a reader does not have to be taught. Orange rather than
 * the strict traffic-light yellow because the Moderate effort chip is already yellow, and two
 * translucent yellow pills side by side would be all but indistinguishable.
 *
 * The icon is what makes the axis legible regardless: the ring, the clock and the tick say "this
 * pill is a status" whatever colour sits next to it.
 *
 * `button` is worn by the action that moves a fix INTO this status, not by a fix already in it — so
 * the button previews the chip the card is about to get, and one map defines the pair. Written as
 * whole class strings rather than assembled from the colour name, because Tailwind only emits
 * classes it can see literally in the source.
 */
const STATUS_STYLES: Record<FixStatus, { chip: string; icon: LucideIcon; button: string }> = {
  open: {
    chip: "red",
    icon: Circle,
    button: "bg-red-500/15 text-red-700 border-red-500/40 hover:bg-red-500/25",
  },
  in_progress: {
    chip: "orange",
    icon: Clock,
    button: "bg-orange-500/15 text-orange-700 border-orange-500/40 hover:bg-orange-500/25",
  },
  resolved: {
    chip: "green",
    icon: CheckCircle,
    button: "bg-green-500/15 text-green-700 border-green-500/40 hover:bg-green-500/25",
  },
};

/** The status a fix moves to when its button is pressed — a cycle, so nothing is a dead end. */
const NEXT_STATUS: Record<FixStatus, FixStatus> = {
  open: "in_progress",
  in_progress: "resolved",
  resolved: "open",
};

/**
 * What the button says, named for the status it moves the fix TO.
 *
 * One family, not three: both forward moves read "Mark <next status>", so the button always names
 * its destination in the same words the Status filter uses. Only the wrap-around breaks the
 * pattern, because "Mark Open" describes the mechanism rather than the intent.
 */
const STATUS_ACTION_LABELS: Record<FixStatus, string> = {
  open: "Mark In Progress",
  in_progress: "Mark Resolved",
  resolved: "Reopen",
};

/**
 * Order recommendations by what still needs doing, matching what the Fixes tab showed before. Work
 * in flight first, then untouched, then anything already resolved.
 *
 * Applied once per diagnosis rather than on every status change — see `useFrozenOrder`.
 */
const STATUS_ORDER: Record<FixStatus, number> = { in_progress: 0, open: 1, resolved: 2 };

/**
 * The status and styling for a fix, defaulting anything unrecognised to Open.
 *
 * `FixStatus` is asserted over an unvalidated JSON response, not proven by it, so a status the
 * backend adds later would otherwise index these maps to `undefined` and throw mid-render. There is
 * no ErrorBoundary above this tab, so that throw would blank the whole app rather than one card.
 */
const statusOf = (status: FixStatus): FixStatus => (STATUS_STYLES[status] ? status : "open");

/** What an axios rejection carries when FastAPI answers with a detail string. */
interface ApiErrorLike {
  response?: { data?: { detail?: string } };
}

const STATUS_OPTIONS = (Object.keys(STATUS_LABELS) as FixStatus[]).map((value) => ({
  value,
  label: STATUS_LABELS[value],
}));

const SEVERITY_OPTIONS = (Object.keys(SEVERITY_PRIORITY) as GapSeverity[]).map((value) => ({
  value,
  label: SEVERITY_PRIORITY[value],
}));

/**
 * One row in the grid: a finding, and the fix for it when the backend produced one.
 *
 * Both a fix and a bare gap normalise into this shape so the filter bar, the counts and the empty
 * state all speak about the same population. Without it the grid counted fixes while the empty
 * state talked about gaps, and a gap that generated no fix appeared nowhere at all.
 */
interface PersonaRecommendation {
  id: string;
  title: string;
  severity: GapSeverity;
  /** Effort band, or "" for a gap with no fix — nothing has been costed yet. */
  effort: string;
  /** A gap with no fix is by definition not started. */
  status: FixStatus;
  /** What the crawl actually found — the "why" line. */
  evidence: string;
  /** The fix to open in the guide, or null when this gap generated none. */
  fix: PersonaFix | null;
}

/**
 * One descriptor per dropdown: how a recommendation is tested against the selected value, and which
 * option it counts towards.
 *
 * Three axes, not the audit tab's five. There is no Scoring Category — a persona report has no
 * scoring pillars — and no Type: the gap rules do carry a category, but technical/content is not a
 * split the persona report needs its reader to make.
 */
const FILTERS: RecommendationFilterSpec<PersonaRecommendation>[] = [
  {
    key: "status",
    label: "Status",
    allLabel: "All Statuses",
    options: STATUS_OPTIONS,
    matches: (item, value) => value === ALL || item.status === value,
    valueOf: (item) => item.status,
  },
  {
    key: "priority",
    label: "Priority",
    allLabel: "All Priorities",
    options: SEVERITY_OPTIONS,
    matches: (item, value) => value === ALL || item.severity === value,
    valueOf: (item) => item.severity,
  },
  {
    key: "effort",
    label: "Effort",
    allLabel: "All Efforts",
    options: EFFORT_FILTER_OPTIONS,
    matches: (item, value) => value === ALL || item.effort === value,
    valueOf: (item) => item.effort,
  },
];

/** `missing_faq_schema` → `missing faq schema`, which is how the old Diagnosis tab read. */
const humanizeGapType = (gapType: string): string => (gapType ?? "").replace(/_/g, " ");

/**
 * Fixes first, then any gap no fix covers.
 *
 * The backend generates fixes from gaps and matches them by rule, but it does not promise one fix
 * per gap — `fixes_degraded` marks the run where the LLM call failed and fixes fell back to rule
 * templates. Anything left unmatched still gets a card, so a finding cannot go missing just because
 * no remedy was written for it. Matching is deliberately generous (rule id _or_ gap type) so the
 * failure mode is a gap quietly folded into its fix, never the same finding rendered twice.
 */
const toRecommendations = (diagnosis: PersonaDiagnosis | null): PersonaRecommendation[] => {
  const fixes = diagnosis?.fixes ?? [];
  const gaps = diagnosis?.gaps ?? [];

  const covered = new Set<string>();
  for (const fix of fixes) {
    if (fix.rule_id) covered.add(`rule:${fix.rule_id}`);
    if (fix.gap_type) covered.add(`type:${fix.gap_type}`);
  }

  const fromFixes = fixes.map((fix) => ({
    id: fix.id,
    title: fix.title,
    severity: fix.severity,
    effort: fix.effort ?? "",
    status: statusOf(fix.status),
    evidence: fix.evidence,
    fix,
  }));

  const fromGaps = gaps
    .filter(
      (gap: PersonaGap) =>
        !covered.has(`rule:${gap.rule_id}`) && !covered.has(`type:${gap.gap_type}`),
    )
    .map((gap: PersonaGap, index: number) => ({
      id: `gap:${gap.rule_id || gap.gap_type}:${index}`,
      title: gap.signal
        ? `${humanizeGapType(gap.gap_type)} — ${gap.signal}`
        : humanizeGapType(gap.gap_type),
      severity: gap.severity,
      effort: "",
      status: "open" as FixStatus,
      evidence: gap.evidence,
      fix: null,
    }));

  return [...fromFixes, ...fromGaps];
};

/**
 * Hold the display order steady for the life of a diagnosis.
 *
 * The sort answers "what needs doing first", which is a question worth answering when a diagnosis
 * arrives and not one worth re-answering mid-session: re-sorting on every status change teleports
 * the card you just clicked to the top of a two-column grid, so you lose your place and the button
 * under the cursor now belongs to a different card. The order is therefore decided once per
 * `diagnosis_id` and cards update in place after that.
 */
const useFrozenOrder = (
  items: PersonaRecommendation[],
  diagnosisId: string | null,
): PersonaRecommendation[] => {
  const [frozen, setFrozen] = useState<{ diagnosisId: string | null; ids: string[] }>({
    diagnosisId: null,
    ids: [],
  });

  if (frozen.diagnosisId !== diagnosisId) {
    setFrozen({
      diagnosisId,
      ids: [...items]
        .sort((a, b) => STATUS_ORDER[a.status] - STATUS_ORDER[b.status])
        .map((item) => item.id),
    });
  }

  return useMemo(() => {
    const rank = new Map(frozen.ids.map((id, index) => [id, index]));
    // An id the frozen order has never seen sorts last rather than jumping to the front.
    const rankOf = (id: string) => rank.get(id) ?? Number.MAX_SAFE_INTEGER;
    return [...items].sort((a, b) => rankOf(a.id) - rankOf(b.id));
  }, [items, frozen]);
};

interface RecommendationCardProps {
  item: PersonaRecommendation;
  reportId: string;
  personaId: string;
  onFixUpdated: (fixId: string, diagnosis: PersonaDiagnosis) => void;
  onShowFix: (fix: PersonaFix) => void;
}

/**
 * One recommendation: the fix to make, with the gap that prompted it as its "why".
 *
 * Structurally the AI Audit's card — priority icon, title, chip row, then the suggested-fix link
 * opposite the state-changing button — so the two screens read as one product rather than two. A
 * gap the backend wrote no fix for keeps the card and loses the two actions, since there is nothing
 * to open and no fix row to PATCH.
 */
const RecommendationCard = ({
  item,
  reportId,
  personaId,
  onFixUpdated,
  onShowFix,
}: RecommendationCardProps): JSX.Element => {
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);
  const { fix } = item;
  const resolved = item.status === "resolved";
  const priority = SEVERITY_PRIORITY[item.severity] ?? "Medium";
  const status = STATUS_STYLES[item.status];
  const StatusIcon = status.icon;
  // The button is styled for where it takes you, not for where the fix is now.
  const nextStatus = NEXT_STATUS[item.status];

  const handleStatusClick = async () => {
    if (!fix) return;
    setUpdating(true);
    setUpdateError(null);
    try {
      const res = await updateFixStatus(reportId, personaId, fix.id, nextStatus);
      onFixUpdated(fix.id, res.data);
    } catch (err) {
      const detail = (err as ApiErrorLike)?.response?.data?.detail;
      setUpdateError(detail || "Failed to update status.");
    } finally {
      setUpdating(false);
    }
  };

  return (
    <div
      className={`p-5 rounded-xl border transition-all ${resolved ? "bg-green-50 border-green-300" : "bg-surface-container-low border-surface-container-high hover:border-primary/30"}`}
    >
      <div className="flex items-start gap-3 mb-2">
        <AlertCircle className={`w-5 h-5 mt-0.5 flex-shrink-0 ${PRIORITY_ICON_COLORS[priority]}`} />
        <h4 className="font-bold text-on-surface first-letter:uppercase">{item.title}</h4>
      </div>
      <div className="pl-8 mb-3 flex items-center gap-2 flex-wrap text-xs text-on-surface-variant">
        <PriorityBadge priority={priority} />
        {item.effort && (
          <Chip color={EFFORT_CHIP_COLORS[item.effort] || "neutral"}>
            {EFFORT_LABELS[item.effort] || item.effort}
          </Chip>
        )}
        <Chip color={status.chip}>
          <StatusIcon className="w-3 h-3" />
          {STATUS_LABELS[item.status]}
        </Chip>
      </div>
      {item.evidence && (
        <p className="pl-8 mb-4 text-sm text-on-surface-variant">
          <span className="font-semibold">Why: </span>
          {item.evidence}
        </p>
      )}
      {fix ? (
        <div className="pl-8 flex items-center justify-between gap-3">
          <button
            onClick={() => onShowFix(fix)}
            className="text-sm font-semibold text-primary hover:underline flex items-center gap-1.5"
          >
            <Sparkles className="w-4 h-4" />
            Suggested Fix
          </button>
          <div className="flex items-center gap-2">
            {updateError && <span className="text-xs text-red-400">{updateError}</span>}
            <button
              onClick={handleStatusClick}
              disabled={updating}
              // Fixed width: the three labels differ in length, so without it the button
              // resizes on every click and no two cards in the grid line up. The width
              // also holds while the spinner is in, instead of shunting the label along.
              className={`px-4 py-2 min-w-[9.5rem] text-sm font-semibold rounded-lg border transition-colors disabled:opacity-50 flex items-center justify-center gap-2 ${STATUS_STYLES[nextStatus].button}`}
            >
              {updating && <Loader className="w-3 h-3 animate-spin" />}
              {STATUS_ACTION_LABELS[item.status]}
            </button>
          </div>
        </div>
      ) : (
        <p className="pl-8 text-sm text-on-surface-variant italic">
          No fix was generated for this gap — re-run the diagnosis to try again.
        </p>
      )}
    </div>
  );
};

interface PersonaRecommendationsProps {
  diagnosis: PersonaDiagnosis | null;
  loading: boolean;
  fetched: boolean;
  error: string | null;
  onStart: () => void;
  /** The diagnosis state setter, taken as a setter so an update can merge rather than replace. */
  onUpdated: Dispatch<SetStateAction<PersonaDiagnosis | null>>;
  personaLabel: string;
  emoji: string;
  score: number;
  reportId: string;
  personaId: string;
}

/**
 * The persona Recommendations tab: what to fix, why, and what it will cost.
 *
 * Replaces the separate Diagnosis and Fixes tabs. Those were two halves of one thing — every fix is
 * generated from a gap and matched to it by rule — so a reader had to hold a gap in their head
 * while hunting for its fix in the other tab. Each card now carries both, and a gap the backend
 * wrote no fix for still gets a card of its own so no finding is lost in the merge.
 */
const PersonaRecommendations = ({
  diagnosis,
  loading,
  fetched,
  error,
  onStart,
  onUpdated,
  personaLabel,
  emoji,
  score,
  reportId,
  personaId,
}: PersonaRecommendationsProps): JSX.Element => {
  const [viewingFix, setViewingFix] = useState<PersonaFix | null>(null);

  const recommendations = useMemo(() => toRecommendations(diagnosis), [diagnosis]);
  const ordered = useFrozenOrder(recommendations, diagnosis?.diagnosis_id ?? null);

  const { filtered, options, values, hasActiveFilter, setFilter, clearFilters } =
    useRecommendationFilters(ordered, FILTERS);

  /**
   * Fold the PATCH response back in, taking only the fix that changed.
   *
   * Swapping in the whole response would let two cards updating at once fight: a response that was
   * read server-side before the other card's write landed does not contain it, and arriving second
   * would revert it until the next fetch. Merging one fix into current state cannot lose the
   * other.
   */
  const handleFixUpdated = useCallback(
    (fixId: string, fresh: PersonaDiagnosis) => {
      const updatedFix = fresh.fixes?.find((candidate) => candidate.id === fixId);
      onUpdated((current) =>
        current && updatedFix
          ? {
              ...current,
              fixes: current.fixes.map((f) => (f.id === fixId ? updatedFix : f)),
            }
          : fresh,
      );
      // Keep the guide open on the fix it was showing, and close it if that fix is gone.
      setViewingFix((viewing) => (viewing?.id === fixId ? (updatedFix ?? null) : viewing));
    },
    [onUpdated],
  );

  return (
    <DiagnosisShell
      loading={loading}
      fetched={fetched}
      diagnosis={diagnosis}
      error={error}
      onStart={onStart}
      personaLabel={personaLabel}
      emoji={emoji}
      score={score}
    >
      <div className="space-y-4">
        {/* {diagnosis?.fixes_degraded && (
          <p className="text-xs text-on-surface-variant italic">
            These fixes are general suggestions — re-run diagnosis for AI-personalized
            recommendations.
          </p>
        )} */}
        <RecommendationFilterBar
          filters={FILTERS}
          values={values}
          options={options}
          hasActiveFilter={hasActiveFilter}
          onChange={setFilter}
          onClear={clearFilters}
          shownCount={filtered.length}
          totalCount={ordered.length}
          noun="recommendations"
        />
        {filtered.length === 0 ? (
          <RecommendationEmptyState
            totalCount={ordered.length}
            hasActiveFilter={hasActiveFilter}
            emptyMessage="No gaps found for this persona."
            onClear={clearFilters}
          />
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {filtered.map((item) => (
              <RecommendationCard
                key={item.id}
                item={item}
                reportId={reportId}
                personaId={personaId}
                onFixUpdated={handleFixUpdated}
                onShowFix={setViewingFix}
              />
            ))}
          </div>
        )}
      </div>
      {viewingFix && (
        <ImplementationGuideModal
          title={viewingFix.title}
          rationale={viewingFix.rationale}
          guide={viewingFix.steps ?? []}
          example={viewingFix.example}
          onClose={() => setViewingFix(null)}
        />
      )}
    </DiagnosisShell>
  );
};

export default PersonaRecommendations;
